/* eslint-disable @typescript-eslint/no-magic-numbers */
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { InstanceProps, create } from 'react-modal-promise';
import { Form, Input, Modal, notification } from 'antd';
import { CloseIcon } from 'components/icons';
import { useEmployee } from 'hooks';
import { User } from 'src/common/mock/user';
import { PersonalInfo, useSavePersonalInfo } from './query';

type EditPersonalInfoModalProps = {
  user: User;
} & InstanceProps<React.MouseEvent<HTMLElement>>;

const EditPersonalInfoModal: FC<EditPersonalInfoModalProps> = ({
  isOpen,
  onResolve,
  onReject,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation('translation');
  const savePersonalInfo = useSavePersonalInfo();
  const { employee } = useEmployee();

  const onCancel = (): void => {
    Modal.confirm({
      content: 'Changes you made so far will not be saved',
      okText: 'Yes, quit',
      onOk: () => onReject(),
      title: 'Quit editing?',
    });
  };

  const onSave = async (): Promise<void> => {
    try {
      const values = form.getFieldsValue() as PersonalInfo;

      await savePersonalInfo(values);
    } catch (e) {
      notification.error({
        message: 'Something has gone wrong. Try again!',
      });

      return;
    }
    onResolve();
    notification.success({
      message: 'Personal info has been saved!',
    });
  };

  return (
    <Modal
      centered
      title={t('Edit personal info')}
      open={isOpen}
      onOk={onSave}
      onCancel={onCancel}
      okText={t('Save')}
      cancelText={t('Cancel')}
      closeIcon={<CloseIcon />}
    >
      <Form
        form={form}
        name={'edit-personal-info-form'}
        layout="vertical"
        initialValues={{
          linkedIn: employee?.linkedIn,
        }}
      >
        <Form.Item
          name="linkedIn"
          label="LinkedIn"
          rules={[
            {
              type: 'url',
              validator(_, value: string, callback) {
                if (
                  !value.match(
                    /^(http(s)?:\/\/)?([\w]+\.)?linkedin\.com\/(pub|in|profile)/gm,
                  )
                ) {
                  callback(
                    'Please enter valid LinkedIn profile url. Example: https://www.linkedin.com/in/user-name-123456789/',
                  );
                }
              },
            },
          ]}
        >
          <Input placeholder="Enter your LinkedIn profile link" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const editPersonalInfoModal = create(EditPersonalInfoModal);
